/**
 * Backtalk Web Audio Engine
 * Handles TTS audio fetching from Cloudflare Workers AI (@cf/deepgram/aura-* models),
 * Web Audio API decoding, audio reversing, pitch-preserved playback speed adjustment, and waveform analysis.
 */

import { CloudflareConfig, VoiceId } from '../types/game';

class AudioEngine {
  private audioCtx: AudioContext | null = null;
  private activeAudio: HTMLAudioElement | null = null;
  private activeSourceNode: MediaElementAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private gainNode: GainNode | null = null;
  private originalBuffer: AudioBuffer | null = null;
  private reversedBuffer: AudioBuffer | null = null;
  private cache: Map<string, { original: AudioBuffer; reversed: AudioBuffer }> = new Map();
  private volume: number = 0.8;
  private playbackSpeed: number = 1.0;
  private cfConfig: CloudflareConfig | null = null;
  private isUsingCloudflareAI: boolean = false;

  constructor() {}

  public setCloudflareConfig(config: CloudflareConfig | null) {
    this.cfConfig = config;
  }

  public isCloudflareActive(): boolean {
    return this.isUsingCloudflareAI;
  }

  private initAudioContext(): AudioContext {
    if (!this.audioCtx) {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      this.audioCtx = new AudioCtxClass();
      this.gainNode = this.audioCtx.createGain();
      this.gainNode.gain.value = this.volume;
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 64;
      this.gainNode.connect(this.analyser);
      this.analyser.connect(this.audioCtx.destination);
    }
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
    return this.audioCtx;
  }

  public setVolume(val: number) {
    this.volume = Math.max(0, Math.min(1, val));
    if (this.gainNode) {
      this.gainNode.gain.value = this.volume;
    }
  }

  public getVolume(): number {
    return this.volume;
  }

  public setPlaybackSpeed(speed: number) {
    this.playbackSpeed = Math.max(0.25, Math.min(2.0, speed));
    if (this.activeAudio) {
      this.activeAudio.playbackRate = this.playbackSpeed;
      this.activeAudio.preservesPitch = true;
      (this.activeAudio as any).webkitPreservesPitch = true;
      (this.activeAudio as any).mozPreservesPitch = true;
    }
  }

  public getPlaybackSpeed(): number {
    return this.playbackSpeed;
  }

  /**
   * Load audio for a given phrase text and voice selection.
   * Uses Cloudflare Workers AI @cf/deepgram/aura-* endpoints (/api/tts).
   * Fallback to Web Speech API synthesis if Cloudflare Workers AI is unavailable/misconfigured.
   */
  public async loadPhraseAudio(phrase: string, voice: VoiceId = 'aura-1'): Promise<boolean> {
    const key = `${phrase.trim().toUpperCase()}_${voice}`;
    if (this.cache.has(key)) {
      const cached = this.cache.get(key)!;
      this.originalBuffer = cached.original;
      this.reversedBuffer = cached.reversed;
      return true;
    }

    const ctx = this.initAudioContext();
    let arrayBuffer: ArrayBuffer | null = null;
    this.isUsingCloudflareAI = false;

    // 1. Try Cloudflare Workers AI route (/api/tts)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.cfConfig?.accountId) {
      headers['X-CF-Account-Id'] = this.cfConfig.accountId;
    }
    if (this.cfConfig?.apiToken) {
      headers['X-CF-Api-Token'] = this.cfConfig.apiToken;
    }

    try {
      const res = await fetch('/api/tts', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text: phrase,
          voice,
          accountId: this.cfConfig?.accountId,
          apiToken: this.cfConfig?.apiToken,
        }),
      });

      if (res.ok) {
        arrayBuffer = await res.arrayBuffer();
        this.isUsingCloudflareAI = true;
      } else {
        const errJson = await res.json().catch(() => ({}));
        console.warn('[AudioEngine] Cloudflare /api/tts endpoint returned status:', res.status, errJson);
      }
    } catch (e) {
      console.warn('[AudioEngine] /api/tts fetch failed:', e);
    }

    // 2. Fallback to client audio synthesizer if Cloudflare Workers AI endpoint is unavailable/misconfigured
    if (!arrayBuffer) {
      console.warn('[AudioEngine] Falling back to Web Speech API synthesizer');
      arrayBuffer = await this.generateFallbackAudio(phrase, ctx);
    }

    if (!arrayBuffer) {
      throw new Error('Failed to generate audio for phrase');
    }

    // 3. Decode Audio Buffer and reverse channel data
    const originalBuffer = await ctx.decodeAudioData(arrayBuffer);
    const reversedBuffer = this.createReversedBuffer(ctx, originalBuffer);

    this.originalBuffer = originalBuffer;
    this.reversedBuffer = reversedBuffer;

    this.cache.set(key, { original: originalBuffer, reversed: reversedBuffer });
    return true;
  }

  /**
   * Creates a reversed copy of an AudioBuffer using channel data reversal
   */
  private createReversedBuffer(ctx: AudioContext, buffer: AudioBuffer): AudioBuffer {
    const numChannels = buffer.numberOfChannels;
    const length = buffer.length;
    const sampleRate = buffer.sampleRate;
    const reversed = ctx.createBuffer(numChannels, length, sampleRate);

    for (let c = 0; c < numChannels; c++) {
      const srcData = buffer.getChannelData(c);
      const destData = reversed.getChannelData(c);
      for (let i = 0; i < length; i++) {
        destData[i] = srcData[length - 1 - i];
      }
    }
    return reversed;
  }

  public playReverse(onEnded?: () => void): boolean {
    if (!this.reversedBuffer) return false;
    return this.playBuffer(this.reversedBuffer, onEnded);
  }

  public playForward(onEnded?: () => void): boolean {
    if (!this.originalBuffer) return false;
    return this.playBuffer(this.originalBuffer, onEnded);
  }

  /**
   * Pitch-preserving playback using HTMLAudioElement & Web Audio API MediaElementAudioSourceNode
   */
  private playBuffer(buffer: AudioBuffer, onEnded?: () => void): boolean {
    const ctx = this.initAudioContext();
    this.stop();

    // Convert AudioBuffer to WAV Blob URL for HTMLAudioElement pitch-preserved playback
    const wavArrayBuffer = audioBufferToWav(buffer);
    const blob = new Blob([wavArrayBuffer], { type: 'audio/wav' });
    const blobUrl = URL.createObjectURL(blob);

    const audio = new Audio(blobUrl);
    audio.playbackRate = this.playbackSpeed;
    audio.preservesPitch = true;
    (audio as any).webkitPreservesPitch = true;
    (audio as any).mozPreservesPitch = true;

    try {
      const source = ctx.createMediaElementSource(audio);
      source.connect(this.gainNode!);
      this.activeSourceNode = source;
    } catch (e) {
      console.warn('MediaElementSource error, playing direct audio element:', e);
    }

    audio.onended = () => {
      URL.revokeObjectURL(blobUrl);
      this.activeAudio = null;
      this.activeSourceNode = null;
      if (onEnded) onEnded();
    };

    audio.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      this.activeAudio = null;
      this.activeSourceNode = null;
      if (onEnded) onEnded();
    };

    this.activeAudio = audio;
    audio.play().catch((err) => {
      console.warn('Playback error:', err);
      if (onEnded) onEnded();
    });

    return true;
  }

  public stop() {
    if (this.activeAudio) {
      try {
        this.activeAudio.pause();
        this.activeAudio.currentTime = 0;
      } catch (_) {}
      this.activeAudio = null;
    }
    if (this.activeSourceNode) {
      try {
        this.activeSourceNode.disconnect();
      } catch (_) {}
      this.activeSourceNode = null;
    }
  }

  public getFrequencyData(dataArray: Uint8Array<any>): void {
    if (this.analyser) {
      this.analyser.getByteFrequencyData(dataArray);
    } else {
      dataArray.fill(0);
    }
  }

  private async generateFallbackAudio(phrase: string, ctx: AudioContext): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve) => {
      const sampleRate = ctx.sampleRate;
      if ('speechSynthesis' in window) {
        const duration = Math.max(1.5, phrase.length * 0.12);
        const numSamples = Math.floor(sampleRate * duration);
        const offlineCtx = new OfflineAudioContext(1, numSamples, sampleRate);
        
        const osc1 = offlineCtx.createOscillator();
        const osc2 = offlineCtx.createOscillator();
        const gain = offlineCtx.createGain();
        const filter = offlineCtx.createBiquadFilter();

        osc1.type = 'sawtooth';
        osc2.type = 'triangle';
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(600, 0);

        const startTime = 0.1;
        const letterTime = (duration - 0.2) / phrase.length;

        for (let i = 0; i < phrase.length; i++) {
          const charCode = phrase.charCodeAt(i);
          const pitch = 130 + (charCode % 25) * 12;
          const t = startTime + i * letterTime;
          osc1.frequency.setValueAtTime(pitch, t);
          osc2.frequency.setValueAtTime(pitch * 1.5, t);
          filter.frequency.setValueAtTime(400 + (charCode % 10) * 150, t);
        }

        gain.gain.setValueAtTime(0, 0);
        gain.gain.linearRampToValueAtTime(0.4, 0.05);
        gain.gain.setValueAtTime(0.4, duration - 0.1);
        gain.gain.linearRampToValueAtTime(0, duration);

        osc1.connect(filter);
        osc2.connect(filter);
        filter.connect(gain);
        gain.connect(offlineCtx.destination);

        osc1.start(0);
        osc2.start(0);
        osc1.stop(duration);
        osc2.stop(duration);

        offlineCtx.startRendering().then((renderedBuffer) => {
          const wavArrayBuffer = audioBufferToWav(renderedBuffer);
          resolve(wavArrayBuffer);
        });
      } else {
        const emptyCtx = new OfflineAudioContext(1, sampleRate * 1, sampleRate);
        emptyCtx.startRendering().then((b) => resolve(audioBufferToWav(b)));
      }
    });
  }
}

function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1;
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;

  const data = buffer.getChannelData(0);
  const dataLength = data.length * bytesPerSample;
  const bufferLength = 44 + dataLength;
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < data.length; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return arrayBuffer;
}

export const audioEngine = new AudioEngine();
